import {
  extractBemobiErrorCode,
  isBemobiSessionLoadError
} from './bemobi-checkout-error.util';
import {
  BemobiCheckoutMessage,
  BemobiCheckoutMessageType
} from '../models/bemobi-checkout-message.model';

const BEMOBI_MESSAGE_TYPES: BemobiCheckoutMessageType[] = [
  'checkout:success',
  'checkout:error',
  'checkout:close',
  'checkout:action',
  'checkout:resize'
];

const BEMOBI_FAILURE_STATUSES = new Set([
  'FAILED',
  'DENIED',
  'DROPPED',
  'ERROR',
  'CANCELLED',
  'CANCELED'
]);

const BEMOBI_SUCCESS_STATUSES = new Set([
  'PAID',
  'APPROVED',
  'AUTHORIZED',
  'CAPTURED',
  'SUCCESS',
  'COMPLETED',
  'CONFIRMED',
  'SETTLED'
]);

function isBemobiMessageType(
  value: string
): value is BemobiCheckoutMessageType {
  return BEMOBI_MESSAGE_TYPES.includes(value as BemobiCheckoutMessageType);
}

function normalizeMessageData(data: unknown): Record<string, unknown> | null {
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data);
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  }

  if (typeof data === 'object' && data !== null) {
    return data as Record<string, unknown>;
  }

  return null;
}

function getResizeHeight(message: Record<string, unknown>): number | null {
  if (typeof message['height'] === 'number') {
    return message['height'];
  }

  const payload = message['payload'];

  if (
    typeof payload === 'object' &&
    payload !== null &&
    typeof (payload as Record<string, unknown>)['height'] === 'number'
  ) {
    return (payload as Record<string, unknown>)['height'] as number;
  }

  const nestedData = message['data'];

  if (
    typeof nestedData === 'object' &&
    nestedData !== null &&
    typeof (nestedData as Record<string, unknown>)['height'] === 'number'
  ) {
    return (nestedData as Record<string, unknown>)['height'] as number;
  }

  return null;
}

function normalizeStatus(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  return value.trim().toUpperCase();
}

function getMessageStatus(message: Record<string, unknown>): string | null {
  return (
    normalizeStatus(message['status']) ??
    normalizeStatus(
      typeof message['payload'] === 'object' && message['payload'] !== null
        ? (message['payload'] as Record<string, unknown>)['status']
        : null
    )
  );
}

function hasPayments(message: Record<string, unknown>): boolean {
  return Array.isArray(message['payments']) && message['payments'].length > 0;
}

function normalizeIncomingType(
  message: Record<string, unknown>
): string | null {
  const rawType = message['type'];

  if (typeof rawType !== 'string') {
    return null;
  }

  switch (rawType) {
    case 'IFRAME_RESIZE':
    case 'resizeBottomSheet':
      return 'checkout:resize';
    case 'success':
      return 'checkout:success';
    case 'error':
      return 'checkout:error';
    case 'close':
    case 'hideBottomSheet':
      return 'checkout:close';
    case 'action':
      return 'checkout:action';
    default:
      return rawType;
  }
}

function parseSessionLoadError(
  message: Record<string, unknown>
): BemobiCheckoutMessage | null {
  const sessionErrorCode = extractBemobiErrorCode(message);

  if (
    sessionErrorCode === null ||
    !isBemobiSessionLoadError(sessionErrorCode)
  ) {
    return null;
  }

  return {
    type: 'checkout:error',
    payload: message,
    errorCode: sessionErrorCode
  };
}

function parseTypedBemobiMessage(
  type: BemobiCheckoutMessageType,
  message: Record<string, unknown>
): BemobiCheckoutMessage | null {
  switch (type) {
    case 'checkout:close':
      return { type: 'checkout:close' };
    case 'checkout:resize': {
      const height = getResizeHeight(message);

      if (height === null || height <= 0) {
        return null;
      }

      return { type: 'checkout:resize', height };
    }
    case 'checkout:success':
      return {
        type: 'checkout:success',
        payload: message['payload'] ?? message
      };
    case 'checkout:error': {
      const errorCode = extractBemobiErrorCode(message);

      return {
        type: 'checkout:error',
        payload: message['payload'] ?? message,
        ...(errorCode !== null ? { errorCode } : {})
      };
    }
    case 'checkout:action':
      return {
        type: 'checkout:action',
        payload: message['payload'] ?? message
      };
    default:
      return null;
  }
}

function parseBemobiSourcePaymentMessage(
  message: Record<string, unknown>
): BemobiCheckoutMessage | null {
  if (message['bemobiSource'] !== true) {
    return null;
  }

  const status = getMessageStatus(message);

  if (status !== null && BEMOBI_FAILURE_STATUSES.has(status)) {
    return {
      type: 'checkout:error',
      payload: message,
      errorCode: status.toLowerCase()
    };
  }

  if (status !== null && BEMOBI_SUCCESS_STATUSES.has(status)) {
    return {
      type: 'checkout:success',
      payload: message
    };
  }

  if (
    hasPayments(message) &&
    (status === null || !BEMOBI_FAILURE_STATUSES.has(status))
  ) {
    return {
      type: 'checkout:success',
      payload: message
    };
  }

  return null;
}

export function parseBemobiMessage(
  data: unknown
): BemobiCheckoutMessage | null {
  const message = normalizeMessageData(data);

  if (message === null) {
    return null;
  }

  const sessionLoadError = parseSessionLoadError(message);

  if (sessionLoadError !== null) {
    return sessionLoadError;
  }

  const normalizedType = normalizeIncomingType(message);

  if (normalizedType !== null && isBemobiMessageType(normalizedType)) {
    return parseTypedBemobiMessage(normalizedType, message);
  }

  const paymentMessage = parseBemobiSourcePaymentMessage(message);

  if (paymentMessage !== null) {
    return paymentMessage;
  }

  return null;
}

export function isAllowedBemobiOrigin(
  origin: string,
  allowedOrigins: string[]
): boolean {
  return allowedOrigins.includes(origin);
}
